export type PlaybackBehavior = 'nothing' | 'lower-volume' | 'pause';

export interface CaptionSpeechSettings {
  enabled: boolean;
  voiceURI: string;
  rate: number;
  volume: number;
  playbackBehavior: PlaybackBehavior;
  pauseOnOverrun: boolean;
}

const PREFIX = 'player.captions.speech.';
const VALID_BEHAVIORS: PlaybackBehavior[] = ['nothing', 'lower-volume', 'pause'];

const DEFAULTS: CaptionSpeechSettings = {
  enabled: false,
  voiceURI: '',
  rate: 1.0,
  volume: 1.0,
  playbackBehavior: 'nothing',
  pauseOnOverrun: false,
};

export function loadSpeechSettings(): CaptionSpeechSettings {
  try {
    const enabled = localStorage.getItem(PREFIX + 'enabled') === 'true';
    const voiceURI = localStorage.getItem(PREFIX + 'voiceURI') ?? DEFAULTS.voiceURI;

    let rate = DEFAULTS.rate;
    const savedRate = localStorage.getItem(PREFIX + 'rate');
    if (savedRate !== null) {
      const parsed = parseFloat(savedRate);
      if (!isNaN(parsed)) {
        rate = Math.max(0.1, Math.min(2.0, parsed));
      }
    }

    let volume = DEFAULTS.volume;
    const savedVolume = localStorage.getItem(PREFIX + 'volume');
    if (savedVolume !== null) {
      const parsed = parseFloat(savedVolume);
      if (!isNaN(parsed)) {
        volume = Math.max(0, Math.min(1, parsed));
      }
    }

    let playbackBehavior = DEFAULTS.playbackBehavior;
    const savedBehavior = localStorage.getItem(PREFIX + 'playbackBehavior');
    if (savedBehavior !== null && VALID_BEHAVIORS.includes(savedBehavior as PlaybackBehavior)) {
      playbackBehavior = savedBehavior as PlaybackBehavior;
    }

    const pauseOnOverrun = localStorage.getItem(PREFIX + 'pauseOnOverrun') === 'true';

    return { enabled, voiceURI, rate, volume, playbackBehavior, pauseOnOverrun };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveSpeechSettings(settings: Partial<CaptionSpeechSettings>): void {
  try {
    if (settings.enabled !== undefined) {
      localStorage.setItem(PREFIX + 'enabled', String(settings.enabled));
    }
    if (settings.voiceURI !== undefined) {
      localStorage.setItem(PREFIX + 'voiceURI', settings.voiceURI);
    }
    if (settings.rate !== undefined) {
      localStorage.setItem(PREFIX + 'rate', String(settings.rate));
    }
    if (settings.volume !== undefined) {
      localStorage.setItem(PREFIX + 'volume', String(settings.volume));
    }
    if (settings.playbackBehavior !== undefined) {
      localStorage.setItem(PREFIX + 'playbackBehavior', settings.playbackBehavior);
    }
    if (settings.pauseOnOverrun !== undefined) {
      localStorage.setItem(PREFIX + 'pauseOnOverrun', String(settings.pauseOnOverrun));
    }
  } catch {
    // localStorage not available
  }
}

export function isSpeechSynthesisSupported(): boolean {
  return 'speechSynthesis' in window;
}

export function getAvailableVoices(): SpeechSynthesisVoice[] {
  if (!isSpeechSynthesisSupported()) return [];
  return speechSynthesis.getVoices();
}

export function stripHtmlTags(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
